import {
  Box,
  Card,
  TextField,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api/api";


const fieldSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiInputBase-input": {
      width: "100% !important",
      textAlign: "left !important",
    },
    "& .MuiInputBase-inputMultiline": {
      width: "100% !important",
      textAlign: "left !important",
      display: "block !important",
    },
    "& textarea": {
      width: "100% !important",
      textAlign: "left !important",
    },
  };

  const numberFieldSx = {
    ...fieldSx,
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };

  const selectSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiSelect-select": {
      textAlign: "left !important",
      width: "100% !important",
    },
  };

export default function BadgeForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    key: "",
    category: "",
    // rarity_tier: "",
    requirement_type: "",
    requirement_value: "",
  });

  const categoryOptions = [
    { _id: "consistency", label: "Consistency" },
    { _id: "time_investment", label: "Time Investment" },
    { _id: "completion", label: "Completion" },
    { _id: "effort", label: "Effort" },
  ];

  const rarityOptions = [
    { _id: "common", label: "Common" },
    { _id: "rare", label: "Rare" },
    { _id: "elite", label: "Elite" },
  ];

  const requirementOptions = [
    { _id: "sessions_count", label: "Sessions Completed" },
    { _id: "streak_days", label: "Streak Days" },
    { _id: "hours_painted", label: "Hours Painted" },
    { _id: "projects_completed", label: "Projects Completed" },
    { _id: "diamonds_placed", label: "Diamonds Placed" },
  ];

  const queryParams = new URLSearchParams(location.search);
  const badgeId = queryParams.get("badgeId");
    console.log("queryParams: ", queryParams, badgeId);
    const isEditMode = Boolean(badgeId);


    const getBadgeDefinitionDetails = async () => {
        if(!badgeId){
            return;
        }
        try {
            setIsLoading(true);

            const response = await api.getBadgeDefinitionDetails({badgeId: badgeId});

            if(response.status === 1){
            const data = response?.data;  
            setFormData({
                name: data?.name,
                key: data?.key,
                category: data?.category,
                // rarity_tier: data?.rarity_tier,
                requirement_type: data?.requirement_logic?.type,
                requirement_value: data?.requirement_logic?.threshold,
            });
            }
            
        } catch (error) {
            console.error("Error fetching badge definition details:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const loginData = JSON.parse(
            localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
        );

        if (!loginData) {
            navigate("/authentication/sign-in");
        } else {
            if(badgeId){
                getBadgeDefinitionDetails();
            }
        }
    }, [badgeId]);

  const generateKey = (name) =>
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "name") {
        return { ...prev, name: value, key: generateKey(value) };
      }
      return { ...prev, [name]: value };
    });
  };

   const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const payload = {
        name: formData.name,
        key: formData.key,
        category: formData.category,
        requirement_logic: {
          type: formData.requirement_type,
          threshold: Number(formData.requirement_value),
        },
      };

      let res;

      if (isEditMode) {
        res = await api.updateBadgeDefinition({
          badgeId,
          ...payload,
        });
      } else {
        res = await api.createBadgeDefinition(payload);
      }

      if (res.status === 1) {
        navigate("/badges");
      } else {
        alert(res.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SoftBox py={3} display="flex" justifyContent="center">
      <SoftBox width="100%" maxWidth="850px">
        <Card sx={{ overflow: "visible", border: "1px solid #f0f2f5" }}>
          <SoftBox p={3}>
            
            {/* COMPACT HEADER */}
            <SoftBox mb={4} display="flex" flexDirection="column">
              <SoftTypography variant="h6" fontWeight="bold" color="dark" sx={{ fontSize: "1.1rem" }}>
                Badge Configuration
              </SoftTypography>
              <SoftTypography variant="caption" color="text" fontWeight="regular">
                Set the name, rarity, and unlock criteria for this reward.
              </SoftTypography>
            </SoftBox>

            <form onSubmit={handleSubmit}>
              
              {/* CORE IDENTITY */}
              <Box mb={4}>
                <Box display="flex" alignItems="center" mb={2.5}>
                  <SoftTypography variant="button" fontWeight="medium" sx={{ minWidth: "160px" }}>
                    Public Name
                  </SoftTypography>
                  <TextField
                    fullWidth={true}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. 200 Sessions completed"
                    sx={fieldSx}
                  />
                </Box>

                {/* <Box display="flex" alignItems="center">
                  <SoftTypography variant="button" fontWeight="medium" sx={{ minWidth: "160px" }}>
                    System ID Key
                  </SoftTypography>
                  <TextField
                    fullWidth
                    value={formData.key}
                    disabled
                    sx={{ 
                      "& .MuiInputBase-root": { height: "42px", backgroundColor: "#f8f9fa" },
                      "& .Mui-disabled": { "-webkit-text-fill-color": "#adb5bd", fontStyle: "italic" } 
                    }}
                  />
                </Box> */}
              </Box>

              {/* META ATTRIBUTES */}
              <Box display="flex" gap={4} mb={4} pt={2} borderTop="1px solid #f0f2f5">
                <Box flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="secondary" mb={1} display="block">
                    Badge Category
                  </SoftTypography>
                  <FormControl fullWidth sx={selectSx}>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ height: "42px" }}
                    >
                      <MenuItem value="" disabled>Select category</MenuItem>
                      {categoryOptions.map((opt) => (
                        <MenuItem key={opt._id} value={opt._id}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* <Box flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="secondary" mb={1} display="block">
                    Rarity Tier
                  </SoftTypography>
                  <FormControl fullWidth sx={selectSx}>
                    <Select
                      name="rarity_tier"
                      value={formData.rarity_tier}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ height: "42px" }}
                    >
                      <MenuItem value="" disabled>Select tier</MenuItem>
                      {rarityOptions.map((opt) => (
                        <MenuItem key={opt._id} value={opt._id}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box> */}
              </Box>

              {/* UNLOCK CRITERIA BLOCK */}
              <Box
                sx={{
                  backgroundColor: "#fcfdff",
                  p: 3,
                  borderRadius: "12px",
                  mb: 4,
                  border: "1px solid #eef2f8",
                }}
              >
                <SoftTypography variant="button" fontWeight="bold" color="info" textTransform="uppercase" display="block" mb={2}>
                  Unlocking Requirements
                </SoftTypography>

                <Box display="flex" gap={3} alignItems="flex-end">
                  <Box flex={2}>
                    <SoftTypography variant="caption" fontWeight="bold" mb={0.8} display="block">
                      Condition Type
                    </SoftTypography>
                    <FormControl fullWidth sx={{ ...fieldSx, backgroundColor: "white" }}>
                      <Select
                        name="requirement_type"
                        value={formData.requirement_type}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ height: "42px" }}
                      >
                        <MenuItem value="" disabled>Choose tracking metric...</MenuItem>
                        {requirementOptions.map((opt) => (
                          <MenuItem key={opt._id} value={opt._id}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box flex={1}>
                    <SoftTypography variant="caption" fontWeight="bold" mb={0.8} display="block">
                      Target Goal
                    </SoftTypography>
                    <TextField
                      fullWidth
                      type="number"
                      name="requirement_value"
                      value={formData.requirement_value}
                      placeholder="Amount"
                      onChange={(e) => {
                            const val = e.target.value;

                            // allow only digits (0–9)
                            if (val === "" || /^[0-9]+$/.test(val)) {
                            handleChange({
                                target: {
                                name: "requirement_value",
                                value: val,
                                },
                            });
                            }
                        }}
                        onKeyDown={(e) => {
                            // block everything except digits
                            if (
                            !/[0-9]/.test(e.key) &&
                            ![
                                "Backspace",
                                "Delete",
                                "Tab",
                                "ArrowLeft",
                                "ArrowRight",
                                "ArrowUp",     // ✅ allow
                                "ArrowDown",
                            ].includes(e.key)
                            ) {
                            e.preventDefault();
                            }
                        }}
                        inputProps={{
                            inputMode: "numeric", // mobile numeric keyboard
                            pattern: "[0-9]*",
                        }}
                        sx={numberFieldSx}
                    />
                  </Box>
                </Box>
              </Box>

              {/* ACTION FOOTER */}
              <Box display="flex" justifyContent="flex-end" pt={2}>
                <SoftButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={isSubmitting}
                  sx={{ width: "180px" }}
                >
                  {isSubmitting && (
                    <CircularProgress size={18} color="inherit" style={{marginRight: "4px"}} />
                )}
                {isSubmitting
                    ? isEditMode
                        ? "Updating..."
                        : "Saving..."
                    : isEditMode
                    ? "Update Badge"
                    : "Confirm & Save"}
                </SoftButton>
              </Box>

            </form>
          </SoftBox>
        </Card>
      </SoftBox>
    </SoftBox>
  );
}